<script>
    import User from "../../user/data/User";
    import API from "../../../../API";

    import DialogBase from "../../../dialog/components/dialog-base.vue";
    import DialogHeader from "../../../dialog/components/dialog-header.vue";
    import DialogBody from "../../../dialog/components/dialog-body.vue";
    import DialogActionbar from "../../../dialog/components/dialog-actionbar.vue";

    import ConfirmDialog from "../../../dialog/confirm-dialog.vue";

    import Loading from "../../../common/loading.vue";
    import IconTooltip from "../../../common/icon-tooltip.vue";
    import MyuserAvatar from "./myuser-avatar.vue";
    import MyuserFileUpload from "./myuser-file-upload.vue";
    import MyuserTotpDialog from "./myuser-totp-dialog.vue";
    import MyuserCard from "./myuser-card.vue";
    import DialogButton from "../../../dialog/components/dialog-button.vue";
    import toaster from "../../../common/toaster";

    export default {
        name: 'myuser-dialog',
        components: {
            DialogButton,
            MyuserCard,
            DialogBase, DialogHeader, DialogBody, DialogActionbar,
            ConfirmDialog, Loading, IconTooltip,
            MyuserAvatar, MyuserFileUpload, MyuserTotpDialog,
        },
        data() {
            return {
                user: new User(),
                showDialog: false,
                loading: false,
                saving: false,
                invalid: false,

                showFileUploadDialog: false,
                showConfirmDeleteImageDialog: false,
                showConfirmDeactivateTOTPDialog: false,
                showTOTPDialog: false,

                tConfirmDeleteTOTP: Lang.langGet('Do you really want to deactivate your two factor authentication?'),
                tConfirmDeleteTOTPTitle: Lang.langGet('Confirm deactivation'),
                tSetupTOTP: Lang.langGet('Setup Two Factor Authentication'),
                tNewSeed: Lang.langGet('New Seed'),
                tUpdateSeed: Lang.langGet('Update Seed'),
                tDeactivateSeed: Lang.langGet('Deactivate'),
                tTitle: Lang.langGet('Edit my profile')
            }
        },
        methods: {
            show() {
                this.showDialog = true;
            },
            hide() {
                this.showDialog = false;
            },
            handleChange(property, value, invalid) {
                // console.log(`${property} has changed to ${value} and is [${invalid ? 'invalid' : 'valid'}]`);
                this.user[property] = value;
                this.invalid = invalid;
            },
            handleAbortDeactivateTOTP() {
                this.showConfirmDeactivateTOTPDialog = false;
            },
            handleConfirmDeactivateTOTP: async function() {
                this.showConfirmDeactivateTOTPDialog = false;

                const url = '/totp/disable';
                const api = new API(url);

                const {errored} = await api.post();

                if (!errored) {
                    this.user.totpActive = false;
                }
            },
            async handleSave(croppedImage) {
                if (this.invalid) return;
                await this.saveData(croppedImage);
                this.hide();
            },
            handleCancel() {
                this.hide();
            },
            handleShowTOTPDialog() {
                this.showTOTPDialog = true;
            },
            handleCloseTOTPDialog(totpActive) {
                this.showTOTPDialog = false;
                if (totpActive) {
                    this.user.totpActive = true;
                }
            },
            handleDeactivateTOTP() {
                this.showConfirmDeactivateTOTPDialog = true;
            },
            saveData: async function(croppedImage) {
                if(this.saving) return;
                this.saving = true;

                if (croppedImage) {
                    this.user.updateCropperImage(croppedImage);
                }

                const url = '/administration/myuser/edit';
                const api = new API(url);

                const payload = this.user.getPayload();
                const {errored} = await api.post(payload);

                if (!errored) {
                    toaster.success(Lang.langGet('Profile updated successfully.'));
                    this.hide()

                    EaseEventBus.fire('tagSearchUpdated', payload.tagSearch);
                    Laravel.tag_search = payload.tagSearch;

                    EaseEventBus.fire('timepickerModeUpdated', payload.timepickerMode);
                    Laravel.timepickerMode = payload.timepickerMode;
                } else {
                    this.saving = false;
                }
            },
            loadData: async function () {
                this.loading = true;

                const url = '/administration/myuser/edit/data';
                const api = new API(url);

                const data = await api.get();

                this.user.update(data);

                this.user.removePicture = false // why do we need this?
                this.user.removePw();
                this.loading = false;
            },
        },
        mounted() {
            EaseEventBus.listen('showModal', (modal) => {
                if (modal === 'editProfileDialog') {
                    this.show();
                }
            });
            EaseEventBus.listen('hideModal', (modal) => {
                if (modal === 'editProfileDialog') {
                    this.hide();
                }
            });
        },
        watch: {
            showDialog: function(newShowDialog) {
                if (newShowDialog) {
                    this.saving = false;
                    this.loadData();
                }
            }
        }
    }
</script>

<template>
    <dialog-base
        :maxWidth="840"
        :persistent="loading"
        :showDialog="showDialog"
        @close="handleCancel"
    >
        <loading :loading="loading" />
        <confirm-dialog v-if="showConfirmDeactivateTOTPDialog"
            :title="tConfirmDeleteTOTPTitle"
            :message="tConfirmDeleteTOTP"
            :showDialog="showConfirmDeactivateTOTPDialog"
            @cancel="handleAbortDeactivateTOTP"
            @confirm="handleConfirmDeactivateTOTP"
            :no-icons="true"
        />
        <myuser-totp-dialog v-if="showTOTPDialog"
            :user="user"
            :showDialog="showTOTPDialog"
            @cancel="handleCloseTOTPDialog"
        />
        <dialog-header
            :title="tTitle"
            :persistent="loading"
            @close="handleCancel"
        />
        <dialog-body>
            <div class="ma-2">
                <myuser-card
                    :user="user"
                    :invalid="invalid"
                    disableEmail
                    @change="handleChange"
                    @save="handleSave"
                />
            </div>
        </dialog-body>
        <dialog-actionbar
            :disableSave="invalid || saving"
            :processing="loading || saving"
            :confirmIcon="saving ? 'fa-spinner-third fa-spin' : undefined"
            @save="handleSave"
            @close="handleCancel"
        >
            <div v-if="!user.isExternal && !user.isLdap" class="d-flex flex-column">
                {{ tSetupTOTP }}
                <div class="myuser-dialog-row">
                    <dialog-button class="mr-1" @click="handleShowTOTPDialog" color="secondary">
                        {{user.totpActive ? tUpdateSeed : tNewSeed }}
                    </dialog-button>
                    <dialog-button v-if="user.totpActive" color="secondary" @click="handleDeactivateTOTP">
                        {{tDeactivateSeed}}
                    </dialog-button>
                </div>
            </div>
        </dialog-actionbar>
    </dialog-base>
</template>

<style scoped>
    .myuser-dialog-row {
        display: flex;
        flex-direction: row;
    }
</style>
